import { DateTime } from "luxon";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useConfig } from "../config/configContext";
import { convertSecondsTo_H_MM_SS, convertTimeOfDayToSeconds, convertTimeDurationToSeconds, getDateTimeNowMaybeSimulated, getNowMaybeSimulated } from "../utils/time";
import { AudioSchedulerContext } from "../audio/audioSchedulerContext";

const getTimeInSecondsSinceStartOfDay = (timeZone: string) => {
    // const now = DateTime.now().setZone(timeZone);
    // const startOfDay = now.startOf("day");
    // const secondsSinceStartOfDay = Math.floor(now.diff(startOfDay, "seconds").seconds);
    // return secondsSinceStartOfDay;

    //NOTE: the above code is correct for the actual time-in-seconds-since-start-of-day,
    //BUT: because we compare this value with the "startTime" of the session and phases, which are calculated from the "HH:MM" time strings in ``convertTimeOfDayToSeconds()``
    //this brakes when the time changes (e.g. daylight saving time).
    //-> getTimeInSecondsSinceStartOfDay() should return 12*60*60, when it's "12:00 o'clock" - even if that's only 11 hours after midnight in reality because on this day the daylight saving time changed.

    const now = getDateTimeNowMaybeSimulated().setZone(timeZone);
    const hours = now.hour;
    const minutes = now.minute;
    const seconds = now.second;
    return (hours * 60 + minutes) * 60 + seconds;
}

const getTimeInMillisecondsSinceStartOfDay = (timeZone: string) => {
    // const now = DateTime.now().setZone(timeZone);
    // const startOfDay = now.startOf("day");
    // const millisecondsSinceStartOfDay = Math.floor(now.diff(startOfDay, "milliseconds").milliseconds);
    // return millisecondsSinceStartOfDay;

    //NOTE: the above code is correct for the actual time-in-milliseconds-since-start-of-day,
    //BUT: because we compare this value with the "startTime" of the session and phases, which are calculated from the "HH:MM" time strings in ``convertTimeOfDayToSeconds()``
    //this brakes when the time changes (e.g. daylight saving time).
    //To fix this, we let our ``nowContext``'s state ``now`` represent the "time in seconds since start of day, as it were if the actual correct current time would be parsed by convertTimeOfDayToSeconds()" 
    //-> getTimeInMillisecondsSinceStartOfDay() should return 12*60*60*1000, when it's "12:00 o'clock" - even if that's only 11 hours after midnight in reality because on this day the daylight saving time changed.

    const now = getDateTimeNowMaybeSimulated().setZone(timeZone);
    const hours = now.hour;
    const minutes = now.minute;
    const seconds = now.second;
    const millis = now.millisecond;
    return ((hours * 60 + minutes) * 60 + seconds) * 1000 + millis;
}

const getMillisecondsTillNextSecond = (timeZone: string) => {
    const nextSecond = getDateTimeNowMaybeSimulated().plus({ seconds: 1 }).setZone(timeZone);
    const delay = nextSecond.diffNow("milliseconds").milliseconds;
    return delay;
}

export const NowContext = createContext<NowContextType>({ timestampSeconds: 0, now: 0, getNowInMillis: () => 0, updateNow: () => { } });

export type NowContextType = {
    /**
     * The current Date.now() timestamp / 1000.
     * ⚠️ This is not the current time in seconds-of-day!
     * Updates (only ⚠️) every second! 
     * 
     * Used by the ``TimerDisplay``
     */
    timestampSeconds: number;
    /**
     * The current time in seconds-of-day. Updates every second.
     */
    now: number;
    timeshift?: number; // in milliseconds, if time simulation is active
    /**
     * A function to get the current time in milliseconds-of-day. 
     * 
     * Only calculates the time when called.
     * @returns The current time in milliseconds-of-day.
     */
    getNowInMillis: () => number;
    updateNow: () => void;
}

export const NowProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
    const config = useConfig();
    const timezone = config.timezone
    const [now, setNow] = useState(() => getTimeInSecondsSinceStartOfDay(timezone));
    const [audioScheduler] = useContext(AudioSchedulerContext);
    const [timestampSeconds, setTimestampSeconds] = useState(Math.floor(getNowMaybeSimulated() / 1000));
    const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
    const workerRef = useRef<Worker | null>(null);

    const checkForTimeSimulation = useCallback(() => {
        if (typeof window === 'undefined') return;
        
        if (window.setNow === "reset") {
            console.log("🕐 Time simulation reset to real time");
            delete window.timeShift;
            delete window.setNow;
            audioScheduler.stopAllIncludingSilence(); // Stop all audio to reset time simulation
            // Note: updateNow will be called after this function returns

        } else if (typeof window.setNow === 'string' && window.setNow !== "reset") {
            try {
                // Parse time string like "20:11" or "20:11:30"
                const targetSeconds = convertTimeOfDayToSeconds(window.setNow as any);
                const realNow = DateTime.now().setZone(timezone);
                const realSecondsOfDay = (realNow.hour * 60 + realNow.minute) * 60 + realNow.second;
                
                // Calculate the shift needed to make the simulated time match the target
                let timeShiftSeconds = targetSeconds - realSecondsOfDay;
                
                // If target is in the past, assume it means tomorrow
                if (timeShiftSeconds < -12 * 3600) { // less than -12 hours, probably means tomorrow
                    timeShiftSeconds += 24 * 3600;
                }
                
                window.timeShift = timeShiftSeconds * 1000; // Convert to milliseconds
                console.log(`🕐 Time simulation: Set to ${window.setNow} (shift: ${timeShiftSeconds}s)`);
                delete window.setNow; // Clear the trigger
                audioScheduler.stopAllIncludingSilence(); // Stop all audio to reset time simulation
                // Note: updateNow will be called after this function returns

            } catch (error) {
                console.error("🕐 Invalid time format for window.setNow:", window.setNow, error);
                delete window.setNow;
            }
        } else if (typeof window.addNow === 'string') {
            try {
                //if string starts with "-", then parse the rest and make it negative
                const isNegative = window.addNow.startsWith("-");

                // Parse duration string like "05:30" (MM:SS) or "01:05:30" (HH:MM:SS)
                const addSeconds = convertTimeDurationToSeconds(isNegative ? window.addNow.substring(1) : window.addNow as any) * (isNegative ? -1 : 1);
                const addMillis = addSeconds * 1000;
                
                // Add to existing timeShift or create new one
                if (window.timeShift) {
                    window.timeShift += addMillis;
                } else {
                    window.timeShift = addMillis;
                }
                
                console.log(`🕐 Time simulation: Added ${window.addNow} (${addSeconds}s) to time shift. Total shift: ${(window.timeShift / 1000)}s`);
                delete window.addNow; // Clear the trigger
                audioScheduler.stopAllIncludingSilence(); // Stop all audio to reset time simulation
                // Note: updateNow will be called after this function returns

            } catch (error) {
                console.error("🕐 Invalid duration format for window.addNow:", window.addNow, error);
                delete window.addNow;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateNow = useCallback(() => {
        if (timeoutIdRef.current)
            clearTimeout(timeoutIdRef.current);

        // Check for time simulation commands before updating time
        checkForTimeSimulation();

        const millis = getTimeInMillisecondsSinceStartOfDay(timezone);
        const now = Math.floor(millis / 1000)
        setNow(now);
        setTimestampSeconds(Math.floor(getNowMaybeSimulated() / 1000));

        //Schedule next update in application main thread
        const millisTillNextSecond = 1000 - (millis % 1000);
        timeoutIdRef.current = setTimeout(updateNow, millisTillNextSecond);
        // console.debug("⏲️ updateNow", convertSecondsToDurationString(now), { millisTillNextSecond });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getNowInMillis = useCallback(() => getTimeInMillisecondsSinceStartOfDay(timezone),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []);


    useEffect(() => {
        //start web worker to prioritize clock updates in browsers eneergy management
        if (window.Worker) {
            console.debug("⏲️ launch clock worker...");
            workerRef.current = new Worker('/worker/clockWorker.js');
            workerRef.current.onmessage = updateNow;
        }
        //additionally, schedule next update in application main thread
        timeoutIdRef.current = setTimeout(updateNow, getMillisecondsTillNextSecond(timezone));

        return () => {
            if (timeoutIdRef.current)
                clearTimeout(timeoutIdRef.current);
            workerRef.current?.terminate();
            workerRef.current = null;
        };
        // config und timezone werden sich nicht ändern!
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const contextValue = useMemo(() => {
        return {
            timestampSeconds,
            now,
            updateNow,
            getNowInMillis,
            timeshift: window.timeShift ? window.timeShift : undefined // in milliseconds, if time simulation is active
        }
        //timestamp and nowInMillis only change together with now. timezone won't change during app runtime.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [now]);

    return (
        <NowContext.Provider value={contextValue}>
            {children}
        </NowContext.Provider>
    );
}

/**
 * Returns the current time in seconds-of-day.
 * This hook uses internally a ```setInterval``` to update the time every second
 * and thus triggers a re-render every second.
 */
export const useNow = () => {
    return useContext(NowContext).now;
}

/**
 * Returns the current time in seconds-of-day.
 * This hook uses internally a ```setInterval``` to update the time every second
 * and thus triggers a re-render every second.
 */
export const useNowAsString = () => {
    return convertSecondsTo_H_MM_SS(useContext(NowContext).now);
}
